import { Component, OnInit, ViewChild, inject } from '@angular/core';
import { FormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatInput } from '@angular/material/input';
import { MatRadioChange } from '@angular/material/radio';

import {
    MONTH_OPTIONS, RoomStatusReason, RoomStatusReasonSelection, allRoomStatusReasons, closedRoomStatusReasons,
    getRoomStatusReasonLabel as formatRoomStatusReasonLabel
} from 'src/app/models/room.model';
import { environment } from 'src/environments/environment';

@Component({
  selector: 'app-roomstatusreasondialog',
  standalone: false,
  templateUrl: './roomstatusreasondialog.component.html',
  styleUrl: './roomstatusreasondialog.component.css'
})
export class RoomStatusReasonDialogComponent implements OnInit {
  dialogRef = inject<MatDialogRef<RoomStatusReasonDialogComponent, RoomStatusReasonSelection | null>>(MatDialogRef);
  data = inject<{ mode: 'disqualify' | 'closed'; roomName: string; note?: string }>(MAT_DIALOG_DATA);

  form: UntypedFormGroup;
  reasons: RoomStatusReason[];
  RoomStatusReason = RoomStatusReason;
  monthOptions = MONTH_OPTIONS;
  yearOptions: number[];
  showClosedDate = false;
  @ViewChild('customReasonInput') customReasonInput: MatInput;
  getRoomStatusReasonLabel = (reason: RoomStatusReason) => formatRoomStatusReasonLabel(reason, this.data.mode);

  ngOnInit(): void {
    const currentYear = environment.currentAwardYear;
    this.yearOptions = [];
    for (let y = currentYear + 1; y >= 2018; --y) {
      this.yearOptions.push(y);
    }
    this.reasons = this.data.mode === 'closed' ? closedRoomStatusReasons : allRoomStatusReasons;
    this.form = new UntypedFormGroup({
      statusReason: new FormControl<RoomStatusReason>(
        this.data.mode === 'closed' ? RoomStatusReason.PERMANENTLY_CLOSED : null, Validators.required),
      closedMonth: new FormControl<number>(null),
      closedYear: new FormControl<number>(currentYear),
      customReason: new FormControl<string>(''),
      note: new FormControl<string>(this.data.note || '', Validators.required)
    });
    this.showClosedDate = this.form.get('statusReason')?.value === RoomStatusReason.PERMANENTLY_CLOSED;
  }

  needsClosedDate(): boolean {
    return this.form?.get('statusReason')?.value === RoomStatusReason.PERMANENTLY_CLOSED;
  }

  needsCustomReason(): boolean {
    return this.form?.get('statusReason')?.value === RoomStatusReason.OTHER;
  }

  title(): string {
    return this.data.mode === 'closed' ? `Mark closed: ${this.data.roomName}` : `Disqualify: ${this.data.roomName}`;
  }

  submitLabel(): string {
    if (this.data.mode === 'closed') {
      return 'Mark Closed';
    }
    if (this.form?.value.statusReason === RoomStatusReason.PERMANENTLY_CLOSED) {
      return 'Disqualify & Mark Closed';
    }
    return 'Disqualify';
  }

  reasonChanged(event?: MatRadioChange) {
    this.showClosedDate = (event?.value ?? this.form.get('statusReason')?.value) ===
      RoomStatusReason.PERMANENTLY_CLOSED;
    if (this.form.value.statusReason !== RoomStatusReason.OTHER) {
      this.form.patchValue({ customReason: '' });
    } else {
      setTimeout(() => {
        this.customReasonInput?.focus();
      });
    }
  }

  formIsValid(): boolean {
    if (!this.form.value.statusReason || !this.form.value.note?.trim()) {
      return false;
    }
    if (this.needsCustomReason()) {
      return Boolean(this.form.value.customReason?.trim());
    }
    return !this.needsClosedDate() || Boolean(this.form.value.closedYear);
  }

  cancel() {
    this.dialogRef.close(null);
  }

  submit() {
    if (!this.formIsValid()) {
      return;
    }
    const selection: RoomStatusReasonSelection = {
      statusReason: this.form.value.statusReason
    };
    if (this.needsClosedDate()) {
      selection.closedYear = this.form.value.closedYear;
      if (this.form.value.closedMonth) {
        selection.closedMonth = this.form.value.closedMonth;
      }
    }
    if (this.needsCustomReason()) {
      selection.customReason = this.form.value.customReason.trim();
    }
    selection.note = this.form.value.note.trim();
    this.dialogRef.close(selection);
  }
}
